import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CarePlanDto, ClientDto, CreateClient, Invoice, ProjectDto } from '@mys/contracts';
import { api } from './api.js';

/* ============================================================================
   Client queries, and the one write.
   ========================================================================== */

export interface ClientListResponse {
  ok: true;
  items: ClientDto[];
  total: number;
  page: number;
  limit: number;
}

export interface ClientDetailResponse {
  ok: true;
  client: ClientDto;
  projects: ProjectDto[];
  invoices: Invoice[];
  carePlans: CarePlanDto[];
}

export function useClients(q: string, limit = 100) {
  return useQuery({
    queryKey: ['clients', q, limit],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      params.set('limit', String(limit));
      return api.get<ClientListResponse>(`/api/clients?${params.toString()}`, signal);
    },
  });
}

export function useClient(id: string) {
  return useQuery({
    queryKey: ['client', id],
    queryFn: ({ signal }) => api.get<ClientDetailResponse>(`/api/clients/${id}`, signal),
  });
}

export function useCreateClient() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateClient) =>
      api.post<{ ok: true; client: ClientDto }>('/api/clients', input),
    onSuccess: (_data, input) => {
      void client.invalidateQueries({ queryKey: ['clients'] });
      /* The lead moved to "won" in the same transaction, so the board's counts
         and that lead's detail are both stale now. Refetching is cheap; a
         kanban showing a converted customer under "quoted" is not. */
      if (input.leadId) {
        void client.invalidateQueries({ queryKey: ['leads'] });
        void client.invalidateQueries({ queryKey: ['lead', input.leadId] });
      }
    },
  });
}

/** What each project stage is called on screen, in the owner's words. */
export const PROJECT_STATUS_LABELS: Record<ProjectDto['status'], string> = {
  brief: 'Brief',
  preview: 'Preview',
  build: 'Build',
  live: 'Live',
};

export const INVOICE_STATUS_LABELS: Record<Invoice['status'], string> = {
  due: 'Due',
  paid: 'Paid',
  refunded: 'Refunded',
};

export const CARE_PLAN_STATUS_LABELS: Record<CarePlanDto['status'], string> = {
  active: 'Active',
  paused: 'Paused',
  cancelled: 'Cancelled',
};

/** A one-tap WhatsApp message to a client. The owner works on WhatsApp. */
export function waClientHref(client: Pick<ClientDto, 'phone' | 'ownerName'>): string {
  const text = `Namaste ${client.ownerName},`.trim();
  return `https://wa.me/${client.phone.replace(/^\+/, '')}?text=${encodeURIComponent(text)}`;
}

/**
 * What the client still owes, in rupees, GST included.
 *
 * Re-exported rather than written here. This used to sum `amountInr` while the
 * portal summed `grossInr`, so the office and the customer read different
 * totals off the same invoice under the same words — "baaki hai". The one
 * definition lives beside `grossInr` in `@mys/contracts/constants`.
 */
export { outstandingInr } from '@mys/contracts/constants';
